import type { Merge, Simplify, ValueUnion } from '@eturi/ts-helpers'
import type { DeviceOS } from './Device.js'
import type { GranType } from './Granularity.js'

// Raw variants
export type RawAppsDataForDevices = {
	readonly [deviceId: string]: Maybe<RawAppsData>
}

export type RawInstalledApps = {
	readonly [bundleId: string]: Maybe<RawDeviceApp>
}

type RawAppsData = {
	readonly installed_apps: Maybe<RawInstalledApps>
	readonly installed_apps_ts: number
	readonly os: DeviceOS
	readonly updated: boolean
}

export type RawDeviceApp = {
	readonly detailed_version: string
	readonly display_version: string
	readonly installer_package_name?: string
	readonly is_system?: boolean
	readonly supervision_required: boolean
	readonly title: string
}

export type NormalizedAppsDataForDevices = {
	readonly [deviceId: string]: Merge<
		RawAppsData,
		{ readonly installed_apps: { readonly [bundleId: string]: NormalizedDeviceApp } }
	>
}

export type NormalizedDeviceApp = RawDeviceApp & {
	readonly bundleId: string
	// Since we normalize the `SpecialApp` case, we use this to fetch app details from the
	// actual bundleId, rather than the fake "special" one.
	readonly detailsBundleId: string
}

// Decorated versions
export type AppRules = {
	readonly [appId: string]: DeviceApp
}

export type DeviceApp = Simplify<
	NormalizedDeviceApp & {
		readonly bundleId: string
		readonly description: Maybe<string>
		readonly deviceDisplayNames: string[]
		readonly granularity: GranType
		readonly is_system: boolean
		readonly os: DeviceOS
	}
>

export type SpecialApp = ValueUnion<typeof SpecialApp>
export const SpecialApp = {
	APPLE_CAMERA: 'com.apple.camera',
	APPLE_FACETIME: 'com.apple.facetime',
	APPLE_COMBO: 'camera-facetime',
} as const
